import React, { FC, useState, useEffect, useMemo } from "react";
import {
  Collapse,
  Row,
  Typography,
  Modal,
  Form,
  Button,
  Input,
  Switch,
  Select,
  Tooltip,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  PlusOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import {
  Brand,
  DeepPartial,
  DeepPartialWithId,
  MSWordFont,
  MS_WORD_FONT_ARR,
} from "../../data-types";
import styles from "./brands.css";
import { ImageUploader, ImageUploaderImageDisplay } from "../image-uploader";
import { showAutoClosingAlert } from "../../alerts/alerts";
import { newAlertMessage } from "../alert-message";
import { RcFile } from "antd/lib/upload";

const { Panel } = Collapse;
const { confirm } = Modal;

const {
  ["container"]: containerClassName,
  ["add-new-brand"]: addNewBrand,
  ["form"]: formClassName,
  ["button-row"]: buttonRowClassName,
  ["submit-button"]: submitButton,
  ["default-brand-text"]: defaultBrandText,
} = styles as any;

type CreateBrandFunc = (brand: DeepPartial<Brand>) => Promise<void>;
type UpdateBrandFunc = (brand: DeepPartialWithId<Brand>) => Promise<void>;
type DeleteBrandFunc = (brandId: number) => Promise<void>;

export type BrandsProps = {
  brands: Brand[];
  createBrand: CreateBrandFunc;
  updateBrand: UpdateBrandFunc;
  deleteBrand: DeleteBrandFunc;
  loading: boolean;
};

const validateImageBeforeUpload = async (image: RcFile): Promise<boolean | void> => {
  const isLt2M = image.size / 1024 / 1024 < 2;
  if (!isLt2M) {
    showAutoClosingAlert(
      newAlertMessage("error", "You must select an image smaller than 2mb in size")
    );
    return Promise.reject(new Error("Image larger than 2mb"));
  }
};

type CreateOrUpdateBrandFormAction = "create" | "update";
type CreateOrUpdateBrandFormProps<T extends CreateOrUpdateBrandFormAction> = {
  loading: boolean;
  action: T;
  onSubmit: T extends "update" ? UpdateBrandFunc : CreateBrandFunc;
  existingValues: T extends "update" ? Brand : undefined;
};

interface InfoTooltipProps {
  title: string;
}

const InfoTooltip: FC<InfoTooltipProps> = ({ title }) => (
  <Tooltip title={title} placement="right">
    <InfoCircleOutlined style={{ marginLeft: 5 }} />
  </Tooltip>
);

const CreateOrUpdateBrandForm = <T extends CreateOrUpdateBrandFormAction>({
  loading: dataLoading,
  action,
  onSubmit,
  existingValues,
}: CreateOrUpdateBrandFormProps<T>): ReturnType<FC<CreateOrUpdateBrandFormProps<T>>> => {
  let id: number | undefined = undefined;
  let organisationName: string | undefined = undefined;
  let organisationLogo: string | undefined = undefined;
  let footer: string | undefined = undefined;
  let includeTasksInProposalOutput: boolean = false;
  let proposalFont: MSWordFont = "Calibri";
  let isDefault: boolean = false;

  if (existingValues) {
    id = existingValues?.id;
    organisationName = existingValues?.organisationName || undefined;
    organisationLogo = existingValues?.organisationLogo || undefined;
    footer = existingValues?.footer || undefined;
    includeTasksInProposalOutput = existingValues?.includeTasksInProposalOutput;
    proposalFont = existingValues?.proposalFont;
    isDefault = existingValues?.isDefault;
  }

  const [form] = Form.useForm();

  const onReset = () => {
    form.resetFields();
  };

  const initialValues = useMemo(() => {
    return {
      id,
      organisationName,
      organisationLogo: {
        value: null,
        existingImage: organisationLogo || null,
      },
      footer: {
        value: null,
        existingImage: footer || null,
      },
      includeTasksInProposalOutput,
      proposalFont,
      isDefault,
    };
  }, [
    id,
    organisationName,
    organisationLogo,
    footer,
    includeTasksInProposalOutput,
    proposalFont,
    isDefault,
  ]);

  const [formLoading, setFormLoading] = useState<boolean>(false);

  const loading = formLoading || dataLoading;

  const doOnFinish = async (rawValues: any) => {
    try {
      setFormLoading(true);
      const { organisationLogo, footer, ...otherRawValues } = rawValues;

      const values = {
        ...otherRawValues,
        ...(organisationLogo?.value && {
          organisationLogo: organisationLogo?.value,
        }),
        ...(footer?.value && {
          footer: footer?.value,
        }),
        ...(id && { id }),
      };

      await onSubmit(values);
    } finally {
      form.resetFields();
      setFormLoading(false);
    }
  };

  useEffect(() => {
    form.setFieldsValue(initialValues);
    "update" === action && form.validateFields();
  }, [form, initialValues, action]);

  return (
    <Form
      form={form}
      className={formClassName}
      labelAlign="left"
      labelCol={{ span: 10 }}
      wrapperCol={{ span: 17, offset: 1 }}
      onFinish={doOnFinish}
      initialValues={initialValues}
    >
      <Form.Item
        label="Organisation Name"
        name="organisationName"
        rules={[
          {
            required: true,
            message: "Please enter the Organisation name",
          },
        ]}
      >
        <Input disabled={loading} />
      </Form.Item>
      <Form.Item
        label={
          <>
            Organisation Logo
            <InfoTooltip title="Logos work best at resolutions around 600 x 200px" />
          </>
        }
        name="organisationLogo"
        getValueFromEvent={(img) => {
          return {
            existingImage: organisationLogo || null,
            value: img || null,
          };
        }}
      >
        <ImageUploader
          fileTypes={["png", "jpg", "jpeg", "gif"]}
          onBeforeUpload={validateImageBeforeUpload}
          loading={loading}
          allowCrop={true}
        />
      </Form.Item>
      <Form.Item
        label={
          <>
            Proposal Footer
            <InfoTooltip title="Footers will be placed at the bottom of each page of the proposal, and automatically span the width of the page." />
          </>
        }
        name="footer"
        getValueFromEvent={(img) => {
          return {
            existingImage: footer || null,
            value: img || null,
          };
        }}
      >
        <ImageUploader
          fileTypes={["png", "jpg", "jpeg", "gif"]}
          onBeforeUpload={validateImageBeforeUpload}
          loading={loading}
          allowCrop={false}
        />
      </Form.Item>
      <Form.Item
        label="Include tasks in Proposal output"
        name="includeTasksInProposalOutput"
        valuePropName="checked"
        rules={[
          {
            required: true,
            message: "Should tasks be included in the Proposal output?",
          },
        ]}
      >
        <Switch disabled={loading} />
      </Form.Item>
      <Form.Item
        label="Proposal output font"
        name="proposalFont"
        rules={[
          {
            required: true,
            message: "Which font should be used in the Proposal output?",
          },
        ]}
      >
        <Select
          options={MS_WORD_FONT_ARR.map((f) => {
            return {
              value: f,
              label: f,
            };
          })}
          defaultValue={"Calibri"}
        />
      </Form.Item>
      <Form.Item
        label="Default brand"
        name="isDefault"
        valuePropName="checked"
        rules={[
          {
            required: true,
            message: "Is this the default brand?",
          },
        ]}
      >
        <Switch disabled={loading} />
      </Form.Item>
      <Form.Item shouldUpdate className={buttonRowClassName}>
        {() => {
          const submitDisabled =
            loading || form.getFieldsError().filter(({ errors }) => errors.length).length > 0;
          return (
            <div style={{ width: "100%" }}>
              {"update" !== action && (
                <Button onClick={onReset} disabled={loading}>
                  Reset
                </Button>
              )}
              <Button
                className={submitButton}
                type="primary"
                htmlType="submit"
                disabled={submitDisabled}
              >
                {"update" === action ? "Update Brand" : "Add Brand"}
              </Button>
            </div>
          );
        }}
      </Form.Item>
    </Form>
  );
};

const showEditModal = ({
  editBrandModalInfo,
  setEditBrandModalInfo,
  loading,
  updateBrand,
}: {
  editBrandModalInfo: { brand: Brand; show: boolean };
  setEditBrandModalInfo: (payload: { brand: Brand | undefined; show: boolean }) => void;
  loading: boolean;
  updateBrand: UpdateBrandFunc;
}) => {
  const { brand, show } = editBrandModalInfo;
  return (
    <Modal
      title="Edit Brand"
      open={show}
      width="90%"
      style={{ top: "96px" }}
      onCancel={() => {
        setEditBrandModalInfo({
          show: false,
          brand: undefined,
        });
      }}
      footer={[
        <Button
          key="cancel"
          onClick={() => {
            setEditBrandModalInfo({
              show: false,
              brand: undefined,
            });
          }}
        >
          Close
        </Button>,
      ]}
    >
      <CreateOrUpdateBrandForm
        action="update"
        existingValues={brand}
        onSubmit={async (brand) => {
          await updateBrand(brand);
          setEditBrandModalInfo({
            show: false,
            brand: undefined,
          });
        }}
        loading={loading}
      />
    </Modal>
  );
};

export const Brands: FC<BrandsProps> = ({
  brands,
  loading,
  createBrand,
  updateBrand,
  deleteBrand,
}) => {
  const [showCreateBrandModal, setShowCreateBrandModal] = useState<boolean>(false);
  const [editBrandModalInfo, setEditBrandModalInfo] = useState<{
    brand: Brand | undefined;
    show: boolean;
  }>({ brand: undefined, show: false });
  return (
    <div className={containerClassName}>
      <Modal
        title="Create Brand"
        visible={showCreateBrandModal}
        width="90%"
        style={{ top: "96px" }}
        onCancel={() => {
          setShowCreateBrandModal(false);
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setShowCreateBrandModal(false);
            }}
          >
            Close
          </Button>,
        ]}
      >
        <CreateOrUpdateBrandForm
          action="create"
          existingValues={undefined}
          onSubmit={async (brand) => {
            await createBrand(brand);
            setShowCreateBrandModal(false);
          }}
          loading={loading}
        />
      </Modal>
      {editBrandModalInfo?.brand &&
        showEditModal({
          loading,
          editBrandModalInfo: editBrandModalInfo as {
            brand: Brand;
            show: boolean;
          },
          setEditBrandModalInfo,
          updateBrand,
        })}
      <Row gutter={16}>
        <Typography.Title level={5} style={{ marginBottom: 16 }}>
          Brands
        </Typography.Title>
      </Row>
      <Row gutter={16}>
        <Typography.Text
          className={addNewBrand}
          onClick={() => {
            setShowCreateBrandModal(true);
          }}
        >
          <PlusOutlined style={{ marginLeft: "3px" }} />
          <span style={{ marginLeft: "10px" }}>Add Brand</span>
        </Typography.Text>
        {!loading && brands && brands.length > 0 && (
          <Collapse style={{ width: "100%" }}>
            {brands.map((brand) => {
              const { isDefault } = brand;
              const delBrand = () => (
                <DeleteOutlined
                  onClick={async (event) => {
                    event.stopPropagation();
                    confirm({
                      icon: <ExclamationCircleOutlined />,
                      content: (
                        <Typography.Text>
                          Are you sure you want to delete this brand?
                        </Typography.Text>
                      ),
                      onOk: async function () {
                        await deleteBrand(brand.id);
                      },
                      onCancel() {},
                    });
                  }}
                />
              );

              const panelTitle = () => {
                return (
                  <>
                    <span>{brand?.organisationName}</span>
                    <EditOutlined
                      style={{ marginLeft: "10px" }}
                      onClick={(event) => {
                        event.stopPropagation();
                        setEditBrandModalInfo({
                          brand,
                          show: true,
                        });
                      }}
                    />
                    {isDefault && <span className={defaultBrandText}>DEFAULT</span>}
                  </>
                );
              };

              return (
                <Panel key={(brand?.id || 0).toString()} header={panelTitle()} extra={delBrand()}>
                  <Typography.Paragraph strong>Organisation Name</Typography.Paragraph>
                  <Typography.Paragraph>{brand?.organisationName}</Typography.Paragraph>

                  <Typography.Paragraph strong>Organisation Logo</Typography.Paragraph>
                  <ImageUploaderImageDisplay value={brand?.organisationLogo || ""} width={200} />

                  <Typography.Paragraph strong>Proposal Footer</Typography.Paragraph>
                  <ImageUploaderImageDisplay value={brand?.footer || ""} width={200} />

                  <Typography.Paragraph strong>
                    Include Tasks In Proposal Output?
                  </Typography.Paragraph>
                  <Typography.Paragraph>
                    {brand?.includeTasksInProposalOutput ? "Yes" : "No"}
                  </Typography.Paragraph>

                  <Typography.Paragraph strong>Proposal Font</Typography.Paragraph>
                  <Typography.Paragraph>{brand?.proposalFont}</Typography.Paragraph>

                  <Typography.Paragraph strong>Is Default brand?</Typography.Paragraph>
                  <Typography.Paragraph>{brand?.isDefault ? "Yes" : "No"}</Typography.Paragraph>
                </Panel>
              );
            })}
          </Collapse>
        )}
      </Row>
    </div>
  );
};
